/**
 * LinearCategoryExplainer — progressive-disclosure explainer for linear
 * categories.
 *
 * A linear category embodies the "no duplication, no deletion" discipline —
 * every resource must be used exactly once. CSK uses this structure in
 * RES.CONSUME to make double-spending structurally inexpressible.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  LinearResourceLedger,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR["RES.CONSUME"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-linear-category",
  kind: "Categorical structure",
  title: "Linear category",
  subtitle: "No dup · no drop · use-once discipline",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Compact-closed", href: "#explainer-compact-closed" },
  { label: "Dagger compact", href: "#explainer-dagger-compact" },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "Monad", href: "#explainer-monad" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>linear category</strong> is one where every morphism uses each input{" "}
      <em>exactly once</em> — no copying, no discarding. CSK uses this to make double-spending of a
      governed resource structurally inexpressible.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of a physical key that opens exactly one lock and is consumed in the process. In a
        linear setting, variables are like single-use vouchers: you hand them in, they do their job,
        and then they are gone.
      </p>
      <p>
        Contrast with ordinary categories where <em>weakening</em> (discarding an unused input) and{" "}
        <em>contraction</em> (copying an input to use it twice) are free. In linear logic the
        structural rules vanish, and each assumption must be consumed exactly once.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        A <strong>linear category</strong> is a symmetric monoidal category{" "}
        <MathInline>{tex`(C, \otimes, I)`}</MathInline> where there are no natural transformations
        implementing:
      </p>
      <ul>
        <li>
          <strong>Diagonal</strong> <MathInline>{tex`\Delta : A \to A \otimes A`}</MathInline> —
          duplication is absent by construction.
        </li>
        <li>
          <strong>Augmentation</strong> <MathInline>{tex`! : A \to I`}</MathInline> — deletion is
          absent by construction.
        </li>
      </ul>
      <p>
        No diagonals = no cloning; no augmentations = no discarding. The morphisms of a{" "}
        <MathInline>{tex`*`}</MathInline>-autonomous category model linear implication{" "}
        <MathInline>{tex`(A \multimap B)`}</MathInline>.
      </p>
      <LinearResourceLedger accent={ACCENT} />
    </>
  ),
  relevance: (
    <>
      <p>
        CSK uses linear structure in <code>RES.CONSUME</code>. Every governed resource is a linear
        object — it must be consumed exactly once. Morphisms in the linear category are the
        computations that consume these resources, each carrying a typed cost.
      </p>
      <ul>
        <li>
          <strong>CWE-400</strong> (uncontrolled resource consumption): structurally inexpressible —
          every operation carries a typed cost and budget is enforced by the linear structure;
          unbounded consumption cannot be constructed.
        </li>
        <li>
          <strong>CWE-770</strong> (allocation without limits): the linear category enforces that
          every allocation is bounded by a budget witness — an unlimited allocation is an ill-typed
          term.
        </li>
        <li>
          <strong>CWE-834</strong> (excessive iteration): iteration is bounded by the linear cost
          structure — a loop without a decreasing budget witness cannot be constructed.
        </li>
      </ul>
      <p>
        The use-once discipline also helps neutralize <strong>CWE-672</strong> (operation on a
        resource after expiration or release): once a linear resource is consumed, it is removed
        from scope and cannot be referenced again — a second use is not a runtime error but an
        ill-typed term.
      </p>
      <p>
        The <code>CONTAINED</code> mode means once a resource enters the linear envelope, it cannot
        escape without explicit release governed by <code>RES.RELEASE</code>.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon (RES.CONSUME R1)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Intuitionistic Linear Logic in CSK",
      path: "theory/structural/Intuitionistic-Linear-Logic-In-CSK.md",
    },
    {
      label: "RES.CONSUME Neutralization (Agda)",
      path: "agda/Kernel/RES/CONSUME/Neutralization.agda",
    },
  ],
};

export function LinearCategoryExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<LinearResourceLedger accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
